/* eslint-disable camelcase */
/* eslint-disable no-restricted-globals */

import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import jwt from "jwt-decode";
import * as _ from "lodash";

// region Store
import Store from "@store/jotai";
import { api } from "@store/services/rest";
// endregion Store
// region Atoms
import { useSelectClient } from "@atoms/SelectClient";
// endregion Atoms
// region Interfaces
import { User } from "@interfaces/user.interface";
import { Token } from "@interfaces/token.interface";
import { ECommandResult } from "@store/enums/commandResult.enum";
import { CommandResult_User } from "src/store/graphql/helpers/types.graphql";

// endregion Interfaces

interface SignInCredentials {
  email: string;
  password: string;
}

interface AuthState {
  user: User;
}

interface AuthContextData {
  user: User;
  validateToken: (token: string) => boolean;
  getTokenData: (token: string) => any;
  getDecodedToken: () => Token;
  signIn(credentials: SignInCredentials): Promise<CommandResult_User>;
  signOut(): void;
  checkPassword(credentials: SignInCredentials): Promise<boolean>;
  updateUser(): void;
}

const getUser = () => {
  // Get token and lastDataShareAccept from localStorage
  const token = localStorage.getItem("@Topcon:token");
  const lastDataShareAccept = localStorage.getItem("@Topcon:lastDataShareAccept");

  // If token exists, set it in api default headers, get user data from token and set it in state
  if (token) {
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    const tokenData: Token = jwt(token);

    if (tokenData) {

      // Set user data
      const user: User = {
        token,
        email: tokenData.email,
        admin: tokenData.admin === "True",
        lastDataShareAccept: lastDataShareAccept ? new Date(lastDataShareAccept) : null
      };

      return {
        user
      };
    }
  }

  return {} as AuthState;
};

const AuthContext = createContext<AuthContextData>({} as AuthContextData);
const AuthProvider: React.FC = ({ children }) => {
  const { reset: resetClients } = Store.useClients();
  const { reset: resetSelectClient } = useSelectClient();

  const [data, setData] = useState<AuthState>(getUser);

  /**
   * Update user token. This method is used on travel sharing
   */
  const updateUser = useCallback(() => {
    const user = getUser();

    setData(user);
  }, [getUser]);

  /**
   * Log in and store data access
   * @param { email, password }
   */
  const checkPassword = useCallback(async ({ email, password }) => {
    const response = await api.post("/auth/login", {
      email,
      password
    });
    const responseData = response.data;

    return responseData.status === ECommandResult.SUCCESS;
  }, []);

  /**
   * Log in and store data access
   * @param { email, password }
   */
  const signIn = useCallback(async ({ email, password }) => {
    const response = await api.post("/auth/login", {
      email,
      password
    });
    const responseData = response.data;

    if (responseData.status === ECommandResult.SUCCESS) {
      const user = responseData.result;

      // Set token and lastDataShareAccept in localStorage
      localStorage.setItem("@Topcon:token", user.token);
      localStorage.setItem("@Topcon:lastDataShareAccept", user.lastDataShareAccept.toString());

      // Set token to api default headers
      api.defaults.headers.common["Authorization"] = `Bearer ${user.token}`;

      // Set user data in state
      setData({ user });
    }

    return responseData;
  }, []);

  /**
   * Log out and clear store data access
   */
  const signOut = useCallback(() => {
    localStorage.removeItem("@Topcon:token");
    localStorage.removeItem("@Topcon:lastDataShareAccept");
    localStorage.removeItem("@Topcon:google");
    localStorage.removeItem("@Topcon:themeColors");

    // Clean states
    resetClients();
    resetSelectClient();

    setData({} as AuthState);

    window.location.href = "/customer/login";
  }, [resetClients, resetSelectClient]);

  /**
   * Get token decrypted data
   * @param token
   */
  const getTokenData = useCallback((token: string): Token => jwt(token), []);

  /**
   * Verify if token is valid
   * @param token
   */
  const validateToken = useCallback(
    (token: string) => {
      if (!token) return false;

      try {
        const decoded = getTokenData(token);

        if (!decoded.exp) return false;

        return Date.now() < decoded.exp * 1000;
      } catch (e) {
        return false;
      }
    },
    [getTokenData]
  );

  const getDecodedToken = useCallback((): Token => {
    if (!_.isEmpty(data.user)) {
      return jwt(data.user.token);
    }

    return {} as Token;
  }, [data]);

  const provider = useMemo(
    () => ({
      user: data.user,
      signIn,
      signOut,
      checkPassword,
      getTokenData,
      validateToken,
      getDecodedToken,
      updateUser
    }),
    [
      data,
      signIn,
      signOut,
      checkPassword,
      getTokenData,
      validateToken,
      getDecodedToken,
      updateUser
    ]
  );

  return (
    <AuthContext.Provider value={provider}>{children}</AuthContext.Provider>
  );
};

function useAuth(): AuthContextData {
  return useContext(AuthContext);
}

export { AuthProvider, useAuth };
